import '../css/app.css';
import { createApp, h } from 'vue';
import { createStore } from 'vuex';
import { createI18n } from 'vue-i18n';
import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { ZiggyVue } from 'ziggy-js';
import Store from './vue/store/Store';
import mitt from 'mitt';
import HighchartsVue from 'highcharts-vue';
import * as Sentry from '@sentry/vue';
import axios from 'axios';

/*
|--------------------------------------------------------------------------
| DATALAYER
|--------------------------------------------------------------------------
*/
window.dataLayer = window.dataLayer || [];
window.dataLayer.push({
    originalLocation:
        document.location.protocol +
        '//' +
        document.location.hostname +
        document.location.pathname +
        document.location.search,
});

/*
|--------------------------------------------------------------------------
| SHARED CONFIGURATION
|--------------------------------------------------------------------------
*/

let appName = import.meta.env.VITE_APP_NAME || 'Crowdbook';

// Create store
const store = createStore(Store);

// Setup emitter
const emitter = mitt();

/*
|--------------------------------------------------------------------------
| LANGUAGE
|--------------------------------------------------------------------------
*/

const languageFilePaths = {};

Object.entries(import.meta.glob('../../lang/*.json')).forEach(([filePath, moduleSpecifier]) => {
    const lang = filePath.match(/\/lang\/([^/]+)\.json$/)?.[1];
    if (lang && !lang.includes('deleted')) {
        languageFilePaths[lang] = import(`../../lang/${lang}.json`);
    }
});

const loadLanguage = async (lang) => {
    try {
        const messages = await languageFilePaths[lang];
        return messages.default;
    } catch (error) {
        const fallbackMessages = languageFilePaths['en'];
        return fallbackMessages.default;
    }
};

const getLocale = () => {
    return document.documentElement.lang || 'en';
};

const i18n = createI18n({
    legacy: false, // use legacy API
    allowComposition: true, // to use with Composition API
    locale: getLocale(),
    fallbackLocale: 'en',
    messages: {
        [getLocale()]: await loadLanguage(getLocale()),
    },
    globalInjection: true,
    silentTranslationWarn: true,
});

const vendor = () => {
    // Extract the vendor name from the URL
    const currentUrl = window.location.pathname;
    const urlParts = currentUrl.split('/');
    const vendorIndex = urlParts.indexOf('vendors');
    // Check if "vendors" is found in the URL and if there is a vendor name after it
    return vendorIndex !== -1 && urlParts.length > vendorIndex + 1 ? urlParts[vendorIndex + 1] : null;
};

/*
|--------------------------------------------------------------------------
| Global window properties
|--------------------------------------------------------------------------
*/

window.axios = axios;
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

window.__locale__ = getLocale();

/*
|--------------------------------------------------------------------------
| INITIALIZE APPLICATIONS
|--------------------------------------------------------------------------
*/
import { createAppConfig } from './composables/useAppConfig';

const appConfig = createAppConfig(getLocale(), vendor());

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });

        if (import.meta.env.VITE_SENTRY_ENABLED) {
            Sentry.init({
                app,
                dsn: import.meta.env.VITE_SENTRY_DSN,
                environment: import.meta.env.MODE,
                tunnel: '/sentry/tunnel',
                tracesSampleRate: 1.0,
                debug: import.meta.env.VITE_SENTRY_DEBUG,
                // This sets the sample rate to be 10%. You may want this to be 100% while
                // in development and sample at a lower rate in production
                replaysSessionSampleRate: 0.1,
                // If the entire session is not sampled, use the below sample rate to sample
                // sessions when an error occurs.
                replaysOnErrorSampleRate: 1.0,
                integrations: [
                    Sentry.replayIntegration({
                        // Additional SDK configuration goes in here, for example:
                        maskAllText: true,
                        blockAllMedia: true,
                        mutationBreadcrumbLimit: 1000,
                        mutationLimit: 1500,
                    }),
                ],
            });

            // Set global flag for components to check
            window.__sentry_enabled = true;
        }

        if (props.initialPage.props?.tenant?.isTenant) {
            appName = props.initialPage.props?.vendor?.name;
        }

        app.use(plugin);
        app.use(i18n);
        app.use(store);
        app.use(ZiggyVue);
        app.use(HighchartsVue);
        app.use(appConfig);

        app.config.errorHandler = (error, instance, info) => {
            // Send to Sentry if enabled
            if (import.meta.env.VITE_SENTRY_ENABLED) {
                Sentry.withScope((scope) => {
                    scope.setTag('errorBoundary', 'global');
                    scope.setContext('vue', {
                        componentName: instance?.$options.name || instance?.$options.__name || 'Unknown',
                        errorInfo: info,
                        propsData: instance?.$props,
                    });
                    scope.setLevel('error');
                    Sentry.captureException(error);
                });
            } else {
                console.error('Vue error:', error, 'Component:', instance, 'Info:', info);
            }
        };

        // Global get vendor function used in mixind and components in old way. To be removed in the future and use inertia props instead.
        app.config.globalProperties.getCurrentVendor = function () {
            return vendor();
        };
        app.provide('emitter', emitter);
        //Uncomment this to see better Vue warnings in the console
        // app.config.warnHandler= (msg, vm, trace) => {
        //     console.warn(`[Vue warn]: ${msg}${trace}`);
        // },
        app.mount(el);
    },
    progress: {
        color: '#4B5563',
    },
});

async function updateLocale(locale) {
    if (locale && document.documentElement.lang !== locale) {
        // Update HTML lang
        document.documentElement.lang = locale;

        // Update i18n locale
        try {
            // Load the language if not already available
            if (!i18n.global.availableLocales.includes(locale)) {
                const messages = await loadLanguage(locale);
                i18n.global.setLocaleMessage(locale, messages);
            }

            // Switch the locale
            i18n.global.locale.value = locale;
        } catch (error) {
            console.error(`Failed to update i18n locale to ${locale}:`, error);
        }
    }
}

router.on('navigate', async (event) => {
    const locale = event.detail.page.props.app.locale;

    if (locale) {
        await updateLocale(locale);
        document.documentElement.lang = locale;
    }
});
